/**
 * Resolve a spoken/typed place query to a Google Places result (India).
 * Used by voice booking so a successful match can be committed without
 * requiring the customer to tap a suggestion row.
 */

const OK = (google) =>
  google?.maps?.places?.PlacesServiceStatus?.OK || 'OK';

/**
 * @param {string} query
 * @param {object} [deps] optional Google maps inject for tests
 * @returns {Promise<{ok:true, address:string, coords:{lat:number,lng:number}|null, placeId:string|null}|{ok:false, reason:string}>}
 */
const resolvePlaceFromQuery = (query, deps = {}) => {
  const text = String(query || '').trim();
  if (!text) {
    return Promise.resolve({ ok: false, reason: 'empty' });
  }

  const google = deps.google || (typeof window !== 'undefined' ? window.google : null);
  if (!google?.maps?.places?.AutocompleteService || !google?.maps?.places?.PlacesService) {
    return Promise.resolve({ ok: false, reason: 'no-places' });
  }

  const okStatus = OK(google);

  return new Promise((resolve) => {
    const autocomplete = new google.maps.places.AutocompleteService();
    autocomplete.getPlacePredictions(
      {
        input: text,
        componentRestrictions: { country: 'in' },
      },
      (predictions, status) => {
        if (status !== okStatus || !predictions?.length) {
          resolve({ ok: false, reason: 'no-match' });
          return;
        }

        const top = predictions[0];
        const placeId = top.place_id || null;
        if (!placeId || deps.skipDetails) {
          resolve({
            ok: true,
            address: top.description || text,
            coords: null,
            placeId,
          });
          return;
        }

        const holder = deps.attributionNode
          || (typeof document !== 'undefined' ? document.createElement('div') : {});
        const details = new google.maps.places.PlacesService(holder);
        details.getDetails(
          {
            placeId,
            fields: ['formatted_address', 'geometry', 'name', 'place_id'],
          },
          (place, detailStatus) => {
            if (detailStatus === okStatus && place?.geometry?.location) {
              const loc = place.geometry.location;
              const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
              const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
              resolve({
                ok: true,
                address: place.formatted_address || place.name || top.description || text,
                coords: { lat, lng },
                placeId: place.place_id || placeId,
              });
              return;
            }
            resolve({
              ok: true,
              address: top.description || text,
              coords: null,
              placeId,
            });
          },
        );
      },
    );
  });
};

/**
 * Pure helper for voice dialog close rules (unit-tested).
 * Close only after a successful place commit; keep open on processing/error.
 */
const shouldCloseVoiceDialog = ({ committed, cancelled, status }) => {
  if (cancelled) return true;
  if (committed) return true;
  if (status === 'error' || status === 'processing' || status === 'listening') return false;
  return false;
};

module.exports = {
  resolvePlaceFromQuery,
  shouldCloseVoiceDialog,
};
